'use client';

import { Modal } from '@/components/widgets/modal';
import { BettingModeImage } from './components/betting-mode-image';
import { useEffect, useState } from 'react';
import { Button } from '@nf/ui';
import { useTranslations } from 'next-intl';
import { bettingModeModalStyle } from './betting-mode-modal.style';
import { BettingMode, bettingModeSettings } from '@/features/betslip/betting-mode';
import { useAccountInfo } from '@/hooks/query/account';
import { AccountActStatus } from '@nf/utils-common/constants';
import { Icon } from '@/components/icons/svg-icons';
import clsx from 'clsx';
import { BetslipStyle } from '@/hooks/query';
import { BettingStyleImage } from './components/betting-style-image';
import { useBettingPrefer } from '@/hooks';
import { bettingModeService } from '@/services/betting-mode.service';
import { useObservableState } from '@nf/utils-common/hooks';
import { accountService } from '@/services/account.service';
import { useQueryClient } from '@tanstack/react-query';
import { PopUpFeatureId } from '@nf/types/account';
import { revisionNoticeService } from '@/services/revision-notice.service';

const betslipLayout = [
	{ label: 'lbl_Basic', mode: BetslipStyle.basic },
	{ label: 'lbl_Floating', mode: BetslipStyle.floating }
];

export const BettingModeModal = () => {
	const t = useTranslations('General');
	const {
		isShowModal,
		closeModal
	} = useOpenBettingModeModal();
	const [tabIndex, setTabIndex] = useState<0 | 1>(0);
	const { selectedBettingMode, selectBettingMode, selectedBetslipStyle, selectBetslipStyle,  saveBettingPrefer } = useBettingPrefer({
		initialMode: BettingMode.Independent,
		initialStyle: BetslipStyle.basic,
		syncOnCondition: isShowModal,
	});
	const handleButtonOnClick = () => {
		if (tabIndex === 0) {
			setTabIndex(1);
		} else {
			saveBettingPrefer();
			closeModal();
		}
	};

	const tabDataGroup = [
		{
			title: t('lbl_BettingMode1'),
			settings: [...bettingModeSettings].sort((a, b) => a.mode > b.mode ? 1 : -1),
			subTitle: '',
			btnText: t('Next'),
			footerText: t('lbl_BettingMode5'),
			currentMode: selectedBettingMode,
			setCurrentMode: selectBettingMode,
		},
		{
			title: t('lbl_novicesetting03'),
			settings: [...betslipLayout].sort((a, b) => a.mode > b.mode ? 1 : -1),
			subTitle: '',
			btnText: t('lbl_BettingMode4'),
			footerText: t('lbl_novicesetting02'),
			currentMode: selectedBetslipStyle,
			setCurrentMode: selectBetslipStyle,
		},
	]

	return (
		<>
			<Modal
				title={t('lbl_BettingMode')}
				isOpen={isShowModal}
				closeModal={closeModal}
				variant="event"
				className="modal--betting-mode"
				showHeader={false}
				isBackdropClose={true}
			>

				<div className={clsx('betting-mode', bettingModeModalStyle.root)}>
					<Button
						className={bettingModeModalStyle.backButton}
						variant="icon"
						onClick={() => setTabIndex(0)}
						data-active={tabIndex === 1}
					>
						<Icon name="dropdown" />
						<span>{t('lbl_Back')}</span>
					</Button>
					<div className={clsx('betting-mode__container', bettingModeModalStyle.container)}>
						<div className={clsx('betting-mode__header', bettingModeModalStyle.header)}>
							<div className="betting-mode__col">
								<div className={clsx('betting-mode__title', bettingModeModalStyle.title)}>
									{tabDataGroup[tabIndex].title}
								</div>
								<div className={clsx('betting-mode__astronaut', bettingModeModalStyle.img)}></div>
							</div>
						</div>

						<div
							className={clsx(bettingModeModalStyle.panelWrapper)}
							data-active={tabIndex === 1 ? 'true' : undefined}
						>

							{tabDataGroup.map((tabData, index) => (
								<SettingPanelWrap key={index}>
									<div className={clsx('tab tab--pills', bettingModeModalStyle.tab)}>
										{
											tabData.settings.map(setting => {
												return (
													<div className={clsx('tab__item', bettingModeModalStyle.tabItem)}
														data-selected={tabData.currentMode === setting.mode}
														onClick={() => tabData.setCurrentMode(setting.mode)}
														key={setting.mode}
													>
														<span className={clsx('tab__check-icon', bettingModeModalStyle.tabCheckIcon)}><Icon name="ticket-success-line" /></span>
														<span className="tab__text">{t(setting.label)}</span>
													</div>
												)
											})
										}
									</div>
									<BettingImage index={index} currentMode={tabData.currentMode} />
								</SettingPanelWrap>
							))}
						</div>
						<div className="betting-mode__footer">
							<Button
								variant="primary"
								className={clsx(bettingModeModalStyle.btn)}
								onClick={() => handleButtonOnClick()}
							>
								{tabDataGroup[tabIndex].btnText}
							</Button>
							<div className="betting-mode__tips">
								{tabDataGroup[tabIndex].footerText}
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

const useOpenBettingModeModal = () => {
	const { user } = useAccountInfo();
	const userSetting = user?.UserSetting;
	const queryClient = useQueryClient();
	const isShowBettingModeModal = useObservableState(bettingModeService.bettingModeModal$, false);
	const isShowRevisionNoticeModal = useObservableState(revisionNoticeService.onRevisionNoticeModal$, false);

	const openModal = () => bettingModeService.displayBettingModeModal(true);
	const closeModal = () => bettingModeService.displayBettingModeModal(false);

	useEffect(() => {
		// 若 revision notice 彈窗先顯示, 則待它關閉後觸發顯示 betting mode 彈窗
		if (user?.ActStatus === AccountActStatus.Deposited && !isShowRevisionNoticeModal && !accountService.getHasShownFeatureId(userSetting, PopUpFeatureId.BettingMode)) {
			openModal();
			accountService.setHasShownFeatureId(queryClient, userSetting, PopUpFeatureId.BettingMode);
		}
	}, [queryClient, user?.ActStatus, userSetting]);

	return {
		isShowModal: isShowBettingModeModal,
		closeModal
	}
};

const SettingPanelWrap = ({
	children,
}: {
	children: React.ReactNode,
}) => {
	return (
		<div
			className={clsx(
				bettingModeModalStyle.panel,
			)}
		>
			{children}
		</div>
	)
}

const BettingImage = ({ index, currentMode }: { index: number, currentMode: number }) => {
	return (
		<>
			{
				{
					0: <BettingModeImage bettingMode={currentMode} />,
					1: <BettingStyleImage bettingMode={currentMode} />,
				}[index]
			}
		</>
	)
};
